import { first, pick, omit, omitBy, isEmpty, last, uniq, keys, entries } from 'lodash';

import {
  dereferencePipeline,
  findFieldFromGroups,
  flattenPipeline,
} from '@/explore/pipeline/utils';
import { createPipeline } from '@/core/pipeline';
import { PenguinoVersion } from '@/components/form/penguino-input';
import { isDateField, isNumericField } from '@/explore/model/utils';
import { ensureUniqueKey } from '@/core/operation';
import { notNil, unlessNil } from '@/lib/utils';
import { QuerySortDirection } from '@/generated/graphql';
import { isHistogramVisualisation } from '@/core/visualisation/visualisation';
import { isAggregatedVisualisation } from '@/core/visualisation/visualisation';

import {
  AggregatedVisualisation,
  DereferencedPipeline,
  Exploration,
  Field,
  GroupAggregateOperation,
  Grouping,
  Pipeline,
  VisualisationViewOptions,
  BigNumberWithComparisonOptions,
  Aggregation,
  ChartType,
  SeriesViewOptions,
  SimpleVisualisation,
  ChartAxis,
  Sort,
  HistogramVisualisation,
  BasePipeline,
  ValidVisualisation,
} from '../../types';
import { ensureValidAggregations, getAggregationLabel } from '../../utils/aggregation';
import {
  getFieldsWithRelatedFields,
  getFinalStateOrThrow,
  getGroupingOutputKey,
  getPipelineFields,
  isRelationGrouping,
  PipelineStateContext,
} from '../../pipeline/state';
import {
  ensureValidGroupings,
  fillGroupsForTimeSeriesQuery,
  sortDateGroupingsLast,
} from '../../utils/grouping';
import {
  getLastSwitchToRelationIndex,
  isGroupAggregateOperation,
  isKeyedAggregation,
} from '../../pipeline/operation';
import { explodeCombinedKey, isCombinedKey } from '../charts/grouped-chart/utils';

const getUsableGroupAggregate = (pipeline: BasePipeline | DereferencedPipeline) =>
  pipeline.operations.findLast(
    (op) => isGroupAggregateOperation(op) && isGroupAggregateUsable(op, pipeline),
  );

export const getDefaultSortForVisualisation = (
  visualisation: ValidVisualisation,
  fields: Field[],
) => {
  const { mainAxisKey, valueKeys } = visualisation;
  const chartType = getChartType(visualisation, fields);
  const firstValueKey = first(valueKeys);
  return chartType === 'grouped-timeseries'
    ? [] // Let grouping define sorting, TODO: Sort by date col first and refactor nested data handling (SUP-3196)
    : chartType === 'bar'
      ? firstValueKey !== undefined
        ? [{ key: firstValueKey, direction: QuerySortDirection.Desc }]
        : []
      : mainAxisKey !== undefined
        ? [{ key: mainAxisKey, direction: QuerySortDirection.Desc }]
        : [];
};

const appendGroupingSort = (sort: Sort, groups: Grouping[], fields: Field[]): Sort => {
  const sortKeys = new Set(sort.map((s) => s.key));
  const groupingSort = groups
    .map((group) => {
      const key = getGroupingOutputKey(group);
      const field = fields.find((field) => field.key === key);
      return {
        key,
        direction: field?.type === 'Date' ? QuerySortDirection.Desc : QuerySortDirection.Asc,
      };
    })
    .filter((item) => !sortKeys.has(item.key));
  return [...sort, ...groupingSort];
};

type VisualisationQuery = {
  visualisationPipeline: Pipeline;
  queryPipeline: DereferencedPipeline;
  sort: Sort;
  limit?: number;
  groups: Grouping[];
};

// Based on the initial pipeline and visualisation options, return pipeline that returns data suitable
// for visualisation.
export const getQueryForVisualisation = (props: {
  pipeline: Pipeline;
  fields: Field[];
  visualisation: ValidVisualisation;
  exploration: Exploration;
}): VisualisationQuery => {
  const { fields, pipeline, visualisation, exploration } = props;
  switch (true) {
    case isHistogramVisualisation(visualisation, fields):
      return getQueryForHistogramVisualisation({
        pipeline,
        visualisation,
        exploration,
      });
    case isAggregatedVisualisation(visualisation):
      return getQueryForAggregatedVisualisation({
        pipeline,
        fields,
        visualisation,
        exploration,
      });
    default:
      return getQueryForSimpleVisualisation({
        pipeline,
        fields,
        visualisation,
        exploration,
      });
  }
};

const getQueryForSimpleVisualisation = (props: {
  pipeline: Pipeline;
  fields: Field[];
  visualisation: SimpleVisualisation;
  exploration: Exploration;
}): VisualisationQuery => {
  const { pipeline, fields, visualisation, exploration } = props;
  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  const lastGroupAggregate = getUsableGroupAggregate(flattenedPipeline);
  const sort = visualisation.sort ?? getDefaultSortForVisualisation(visualisation, fields);

  const isVertical = visualisation.viewOptions?.orientation === 'vertical';

  if (lastGroupAggregate !== undefined) {
    const modifiedOperation = {
      ...lastGroupAggregate,
      parameters: {
        ...lastGroupAggregate.parameters,
        groups: fillGroupsForTimeSeriesQuery(
          sortDateGroupingsLast(lastGroupAggregate.parameters.groups),
          fields,
        ),
      },
    };
    const operationIdx = flattenedPipeline.operations.indexOf(lastGroupAggregate);
    const visualisationPipeline = {
      ...flattenedPipeline,
      operations: [
        ...flattenedPipeline.operations.slice(0, operationIdx),
        modifiedOperation,
        ...flattenedPipeline.operations.slice(operationIdx + 1),
      ],
    };

    const chartType = getChartType(visualisation, fields);
    const groups = modifiedOperation.parameters.groups;

    return {
      visualisationPipeline,
      queryPipeline: dereferencePipeline(visualisationPipeline, exploration),
      groups,
      sort: appendGroupingSort(sort, groups, fields),
      ...(chartType === 'bar'
        ? isVertical
          ? { limit: groups.length > 1 ? 200 : 100 }
          : { limit: groups.length > 1 ? 40 : 25 }
        : { limit: 1000 }),
    };
  }

  return {
    visualisationPipeline: pipeline,
    queryPipeline: dereferencePipeline(pipeline, exploration),
    groups: [],
    sort,
    ...(getChartType(visualisation, fields) === 'bar'
      ? isVertical
        ? { limit: 100 }
        : { limit: 25 }
      : { limit: 1000 }),
  };
};

const getQueryForAggregatedVisualisation = (props: {
  pipeline: Pipeline;
  fields: Field[];
  visualisation: AggregatedVisualisation;
  exploration: Exploration;
}): VisualisationQuery => {
  const { pipeline, fields, visualisation, exploration } = props;
  const groups = fillGroupsForTimeSeriesQuery(
    sortDateGroupingsLast(visualisation.aggregation.groups),
    fields,
  );
  const sort = visualisation.sort ?? getDefaultSortForVisualisation(visualisation, fields);
  const newPipeline = createPipeline({
    ...pipeline,
    operations: [
      ...pipeline.operations,
      {
        operation: 'groupAggregate',
        parameters: {
          aggregations: visualisation.aggregation.aggregations,
          groups,
        },
      },
    ],
  });
  const isVertical = visualisation.viewOptions?.orientation === 'vertical';
  const chartType = getChartType(visualisation, fields);
  return {
    visualisationPipeline: newPipeline,
    queryPipeline: dereferencePipeline(newPipeline, exploration),
    groups,
    sort: appendGroupingSort(sort, groups, fields),
    ...(chartType === 'bar'
      ? isVertical
        ? { limit: groups.length > 1 ? 200 : 100 }
        : { limit: groups.length > 1 ? 40 : 25 }
      : { limit: 1000 }),
  };
};

const getQueryForHistogramVisualisation = (props: {
  pipeline: Pipeline;
  visualisation: HistogramVisualisation;
  exploration: Exploration;
}): VisualisationQuery => {
  const { pipeline, visualisation, exploration } = props;
  const historgamPipeline = createPipeline({
    ...pipeline,
    operations: [
      ...pipeline.operations,
      {
        operation: 'histogram',
        parameters: {
          key: visualisation.mainAxisKey ?? '',
          numBuckets: 10,
        },
      },
    ],
  });
  return {
    visualisationPipeline: historgamPipeline,
    queryPipeline: dereferencePipeline(historgamPipeline, exploration),
    groups: [],
    limit: 1000,
    sort: props.visualisation.sort ?? [
      { key: props.visualisation.mainAxisKey, direction: QuerySortDirection.Desc },
    ],
  };
};

/**
 * Remove any groups or aggregations that reference fields no longer present in the cell's final
 * pipeline state, so the groupAggregate operation an aggregated visualisation generates stays valid
 * and its final state remains computable.
 */
export const ensureValidVisualisationAggregation = (
  visualisation: AggregatedVisualisation,
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): AggregatedVisualisation => {
  const fields = getFieldsWithRelatedFields(getFinalStateOrThrow(pipeline, ctx), ctx.models);

  const groups = visualisation.aggregation.groups.filter((group) => {
    const { relation } = isRelationGrouping(group) ? group : {};
    return findFieldFromGroups(fields, { key: group.key, relation }) !== undefined;
  });

  const aggregations = visualisation.aggregation.aggregations.filter(
    (aggregation) =>
      !isKeyedAggregation(aggregation) || findFieldFromGroups(fields, aggregation) !== undefined,
  );

  return {
    ...visualisation,
    aggregation: { ...visualisation.aggregation, groups, aggregations },
  };
};

/**
 * Resolve the visualisation's main axis key, falling back to any aggregation group that still
 * resolves to a field when the configured key no longer exists.
 */
const resolveMainAxisKey = (
  visualisation: ValidVisualisation,
  fieldKeys: Set<string>,
): string | undefined => {
  if (visualisation.mainAxisKey !== undefined && fieldKeys.has(visualisation.mainAxisKey)) {
    return visualisation.mainAxisKey;
  }
  if (!isAggregatedVisualisation(visualisation)) {
    return undefined;
  }
  const group = visualisation.aggregation.groups.find((group) =>
    fieldKeys.has(getGroupingOutputKey(group)),
  );
  return group !== undefined ? getGroupingOutputKey(group) : undefined;
};

const stripInvalidBigNumberFields = (
  bigNumber: NonNullable<VisualisationViewOptions['bigNumber']>,
  fields: Set<string>,
): VisualisationViewOptions['bigNumber'] => {
  if (!fields.has(bigNumber.key)) {
    return undefined;
  }

  if (isBigNumberWithComparison(bigNumber) && fields.has(bigNumber.comparisonKey)) {
    return bigNumber;
  }

  if (isBigNumberWithComparison(bigNumber)) {
    const {
      comparisonKey: _comparisonKey,
      comparisonType: _comparisonType,
      comparisonDirection: _comparisonDirection,
      showComparisonLabel: _showComparisonLabel,
      comparisonMode: _comparisonMode,
      ...rest
    } = bigNumber;
    return rest;
  }

  return bigNumber;
};

const stripInvalidViewOptionsFields = (
  viewOptions: VisualisationViewOptions,
  fields: Set<string>,
): VisualisationViewOptions =>
  viewOptions.bigNumber === undefined
    ? viewOptions
    : { ...viewOptions, bigNumber: stripInvalidBigNumberFields(viewOptions.bigNumber, fields) };

/**
 * Remove any aggregation/grouping keys from the visualisation that no longer resolve to fields in the cell's final
 * pipeline state.
 * This is similar to amendVisualisation but only removes things.
 * The output of this is a valid visualisation.
 */
export const stripInvalidVisualisationParts = (
  visualisation: ValidVisualisation,
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): ValidVisualisation => {
  const fields = new Set(
    getVisualisationFields(visualisation, pipeline, ctx).map((field) => field.key),
  );

  const aggregation = isAggregatedVisualisation(visualisation)
    ? ensureValidVisualisationAggregation(visualisation, pipeline, ctx).aggregation
    : undefined;

  const mainAxisKey = resolveMainAxisKey(visualisation, fields);

  const filteredSort = visualisation.sort?.filter((s) => fields.has(s.key));
  const sort = filteredSort !== undefined && filteredSort.length > 0 ? filteredSort : undefined;
  const viewOptions =
    visualisation.viewOptions !== undefined
      ? stripInvalidViewOptionsFields(visualisation.viewOptions, fields)
      : undefined;

  return {
    ...visualisation,
    ...(aggregation !== undefined ? { aggregation } : {}),
    valueKeys: visualisation.valueKeys.filter((key) => fields.has(key)),
    mainAxisKey,
    ...(sort !== undefined ? { sort } : {}),
    ...(viewOptions !== undefined ? { viewOptions } : {}),
  };
};

/**
 * Return the fields produced by a visualisation, kept stable even when the visualisation references
 * fields that no longer exist.
 */
export const getVisualisationFields = (
  visualisation: ValidVisualisation,
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): Field[] => {
  if (!isAggregatedVisualisation(visualisation)) {
    return getPipelineFields(pipeline, ctx);
  }

  const { aggregation } = ensureValidVisualisationAggregation(visualisation, pipeline, ctx);
  const visualisationPipeline = createPipeline({
    ...pipeline,
    operations: [
      ...pipeline.operations,
      {
        operation: 'groupAggregate',
        parameters: {
          aggregations: aggregation.aggregations,
          groups: aggregation.groups,
        },
      },
    ],
  });

  const keys = new Set(
    [
      ...aggregation.groups.map(getGroupingOutputKey),
      ...aggregation.aggregations.map((agg) => agg.property.key),
    ].filter(notNil),
  );
  return getPipelineFields(visualisationPipeline, ctx).filter((field) => keys.has(field.key));
};

export const getVisualisationGroupByField = (field: Field): Grouping => {
  if (field.type === 'Date') {
    return {
      key: field.key,
      fill: true,
      precision: 'month',
    };
  }

  return {
    key: field.key,
  };
};

export const generateVisualisationFromGroupAggregate = (
  groupAggregate: GroupAggregateOperation,
  fields: Field[],
): ValidVisualisation => {
  const groups = sortDateGroupingsLast(groupAggregate.parameters.groups);
  const aggregations = groupAggregate.parameters.aggregations;

  const bigNumberFieldKey =
    groups.length === 0 ? first(getBigNumberFields(fields))?.key : undefined;

  const viewOptions = {
    axes: {
      right: {
        keys: [],
      },
    },
    series: {},
    ...(bigNumberFieldKey !== undefined
      ? {
          bigNumber: { key: bigNumberFieldKey },
        }
      : {}),
  };

  if (aggregations.length === 0) {
    const numericField = fields.find(isNumericField);
    const aggregations =
      numericField !== undefined
        ? [
            {
              type: 'sum' as const,
              key: numericField.key,
              property: {
                key: `sum_${numericField.key}`,
                name: `Sum of ${numericField.name}`,
              },
            },
          ]
        : [
            {
              type: 'count' as const,
              property: {
                key: 'count',
                name: 'Count',
              },
            },
          ];
    return {
      aggregation: {
        aggregations,
        groups,
      },
      ...visualisationKeysFromAggregation({ aggregations, groups }),
      viewOptions,
    };
  }

  return {
    mainAxisKey: unlessNil(last(groups), (lastGroup) => getGroupingOutputKey(lastGroup)),
    valueKeys: visualisationKeysFromAggregation({ aggregations, groups }).valueKeys,
    viewOptions,
  };
};

export const generateVisualisationFromFields = (fields: Field[]): AggregatedVisualisation => {
  const field = fields.find((field) => field.type === 'Date') ?? fields[0];
  const aggregation = {
    aggregations: [
      {
        type: 'count' as const,
        property: {
          key: 'count',
          name: 'Count',
        },
      },
    ],
    groups: [
      isDateField(field)
        ? {
            key: field.key,
            fill: true,
            precision: 'month' as const,
          }
        : {
            key: field.key,
          },
    ],
  };

  const { valueKeys, mainAxisKey } = visualisationKeysFromAggregation(aggregation);

  return {
    aggregation,
    valueKeys,
    mainAxisKey,
    viewOptions: {
      axes: {
        right: {
          keys: [],
        },
      },
      series: {},
    },
  };
};

export const generateVisualisation = (
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): ValidVisualisation => {
  const fields = getPipelineFields(pipeline, ctx);
  if (fields.length === 0) {
    return { mainAxisKey: undefined, valueKeys: [], viewOptions: {} };
  }
  const flattenedPipeline = flattenPipeline(pipeline, ctx.exploration);
  const lastGroupAggregate = getUsableGroupAggregate(flattenedPipeline);
  const shouldUseGroupAggregate =
    lastGroupAggregate !== undefined && fields.some((f) => isNumericField(f));

  return shouldUseGroupAggregate
    ? generateVisualisationFromGroupAggregate(lastGroupAggregate, fields)
    : generateVisualisationFromFields(fields);
};

export const isBigNumberAllowed = (
  visualisation: ValidVisualisation,
  fields: Field[],
  groups: Grouping[],
) => {
  if (isBigNumberEnforced(visualisation, fields, groups)) {
    return true;
  }
  if (isAggregatedVisualisation(visualisation) && visualisation.aggregation.groups.length === 1) {
    const groupKey = getGroupingOutputKey(visualisation.aggregation.groups[0]);
    const field = fields.find((field) => field.key === groupKey);
    return field !== undefined && isDateField(field);
  }

  return (
    groups.length < 2 &&
    fields.find((field) => field.key === visualisation.mainAxisKey)?.type === 'Date'
  );
};

export const isBigNumberEnforced = (
  visualisation: ValidVisualisation,
  fields: Field[],
  groups: Grouping[],
) => {
  if (isAggregatedVisualisation(visualisation) && visualisation.aggregation.groups.length === 1) {
    const groupKey = getGroupingOutputKey(visualisation.aggregation.groups[0]);
    const field = fields.find((field) => field.key === groupKey);
    return field === undefined;
  }

  return (
    groups.length === 0 ||
    fields.find((field) => field.key === visualisation.mainAxisKey) === undefined
  );
};

export const getBigNumberFields = (fields: Field[]) => fields.filter(isNumericField);

export const getBigNumberComparisonDefaults = (key: string) => ({
  comparisonKey: key,
  comparisonDirection: 'higher',
  comparisonType: 'absolute',
  showComparisonLabel: false,
  comparisonMode: 'value',
});

/**
 * Amending functions.
 *
 * These "amend" a visualisation so it stays valid by dropping or replacing references to fields
 * that no longer exist, rather than marking the whole visualisation as invalid. They are the
 * default (non-manual) validation path — see `ensureValidVisualisation` in `utils/validation.ts`.
 */

const amendBigNumberFieldKey = (
  bigNumberFieldKey: string | undefined,
  ctx: {
    fields: Field[];
    mainAxisKey?: string;
    groups: Grouping[];
  },
) => {
  const fields = getBigNumberFields(ctx.fields);

  if (ctx.mainAxisKey === undefined) {
    if (fields.length === 0) {
      throw new Error('No valid big number fields found');
    }
    return fields.find((field) => field.key === bigNumberFieldKey)?.key ?? fields[0]?.key;
  }

  if (bigNumberFieldKey === undefined) {
    return undefined;
  }

  if (ctx.groups.length > 1) {
    return undefined;
  }

  if (
    ctx.mainAxisKey !== undefined &&
    ctx.fields.find((field) => field.key === ctx.mainAxisKey)?.type !== 'Date'
  ) {
    return undefined;
  }

  if (fields.length === 0) {
    throw new Error('No valid big number fields found');
  }
  const isValidField = fields.some((field) => field.key === bigNumberFieldKey);
  return isValidField ? bigNumberFieldKey : first(fields)?.key;
};

export const amendViewOptions = (
  viewOptions: VisualisationViewOptions | undefined,
  ctx: {
    fields: Field[];
    mainAxisKey?: string;
    valueKeys?: string[];
    chartType: string;
    groups: Grouping[];
  },
) => {
  const newOptions: VisualisationViewOptions = { ...viewOptions };

  if (!['grouped-timeseries', 'bar'].includes(ctx.chartType)) {
    delete newOptions.axes;
  }

  newOptions.series = ensureValidSeries(viewOptions?.series);

  const bigNumberKey = amendBigNumberFieldKey(
    newOptions?.bigNumber?.key ?? newOptions?.bigNumberFieldKey,
    ctx,
  );

  // Clean-up of a deprecated field
  delete newOptions.bigNumberFieldKey;

  if (bigNumberKey === undefined) {
    return omit(newOptions, 'bigNumber');
  }

  newOptions.bigNumber = {
    ...newOptions.bigNumber,
    key: bigNumberKey,
  };

  const comparisonKey = isBigNumberWithComparison(newOptions?.bigNumber)
    ? newOptions.bigNumber.comparisonKey
    : undefined;

  if (comparisonKey !== undefined) {
    newOptions.bigNumber = {
      ...getBigNumberComparisonDefaults(bigNumberKey),
      ...newOptions.bigNumber,
    };
  }

  return setBigNumberOptions(newOptions, {
    mainAxisKey: ctx.mainAxisKey,
    valueKeys: ctx.valueKeys ?? [],
  });
};

export const amendSimpleAggregationForExistingAggregation = (
  visualisation: ValidVisualisation,
  operation: GroupAggregateOperation,
  fields: Field[],
) => {
  const { mainAxisKey } = visualisationKeysFromAggregation(operation.parameters);
  const groupingKeys = operation.parameters.groups.map(getGroupingOutputKey);
  const allValidFieldKeys = fields
    .filter((field) => !groupingKeys.includes(field.key) && isNumericField(field))
    .map((field) => field.key);
  const validValueKeys = visualisation.valueKeys.filter((key) => allValidFieldKeys.includes(key));
  const validVisualisation = {
    mainAxisKey,
    valueKeys: validValueKeys.length > 0 ? validValueKeys : allValidFieldKeys,
  };
  return {
    ...validVisualisation,
  };
};

const amendSimpleAggregation = (visualisation: SimpleVisualisation, fields: Field[]) => {
  let mainAxisKey = fields.find((field) => field.key === visualisation.mainAxisKey)?.key;

  if (visualisation.mainAxisKey !== undefined && mainAxisKey === undefined) {
    const mainAxisCandidates = fields.filter(
      (field) => !visualisation.valueKeys.includes(field.key),
    );
    mainAxisKey =
      mainAxisCandidates.find(isDateField)?.key ??
      mainAxisCandidates.find(isNumericField)?.key ??
      first(mainAxisCandidates)?.key;
  }

  const defaultValueKeys = fields.filter(isNumericField).map((f) => f.key);
  const validValueKeys = visualisation.valueKeys.filter((key) =>
    fields.some((field) => field.key === key),
  );

  let valueKeys: string[] = [];
  if (mainAxisKey !== undefined) {
    valueKeys = validValueKeys;
  }
  if (valueKeys.length === 0 && visualisation.mainAxisKey !== undefined) {
    valueKeys = defaultValueKeys;
  }

  return {
    mainAxisKey,
    valueKeys,
  };
};

const amendSort = (
  sort: ValidVisualisation['sort'],
  visualisation: ValidVisualisation,
  fields: Field[],
) => {
  const { mainAxisKey, valueKeys } = visualisation;
  if (sort === undefined || sort.length === 0) {
    return undefined;
  }
  if (getChartType(visualisation, fields) !== 'bar') {
    return undefined;
  }
  const sortKey = first(sort)?.key;
  const validSortKey =
    sortKey !== undefined && fields.some(({ key }) => key === sortKey)
      ? sortKey
      : (first(valueKeys) ?? mainAxisKey);

  return validSortKey !== undefined
    ? [
        {
          key: validSortKey,
          direction: first(sort)?.direction ?? QuerySortDirection.Desc,
        },
      ]
    : undefined;
};

/**
 * Amend a visualisation so it stays renderable against the cell's current pipeline, replacing or
 * dropping stale field references instead of invalidating the whole visualisation. This is the
 * auto-healing behaviour, used when the visualisation is not in `manual` mode.
 */
export const amendVisualisation = (
  visualisation: ValidVisualisation,
  ctx: PipelineStateContext & {
    pipeline: Pipeline;
  },
): ValidVisualisation => {
  let validVisualisation = isAggregatedVisualisation(visualisation)
    ? amendAggregatedVisualisation(visualisation, ctx)
    : amendSimpleVisualisation(visualisation, ctx);

  if (validVisualisation.valueKeys.length === 0) {
    validVisualisation = amendAggregatedVisualisation(
      {
        ...validVisualisation,
        aggregation: {
          aggregations: [
            {
              type: 'count',
              property: {
                key: 'count',
                name: 'Count',
              },
            },
          ],
          groups: [],
        },
        valueKeys: ['count'],
      },
      ctx,
    );
  }

  const fields = getPipelineFields(ctx.pipeline, ctx);

  const { visualisationPipeline, groups } = getQueryForVisualisation({
    pipeline: ctx.pipeline,
    fields,
    visualisation: validVisualisation,
    exploration: ctx.exploration,
  });

  const visualisationFields = getPipelineFields(visualisationPipeline, ctx);

  const chartType = getChartType(validVisualisation, visualisationFields);

  const validViewOptions = amendViewOptions(visualisation.viewOptions, {
    fields: visualisationFields,
    mainAxisKey: validVisualisation.mainAxisKey,
    valueKeys: validVisualisation.valueKeys,
    chartType,
    groups,
  });

  return {
    ...validVisualisation,
    viewOptions: validViewOptions,
  };
};

export const amendSimpleVisualisation = (
  visualisation: Omit<SimpleVisualisation, 'viewOptions'>,
  ctx: PipelineStateContext & {
    pipeline: Pipeline;
  },
): Omit<SimpleVisualisation, 'viewOptions'> => {
  const flattenedPipeline = flattenPipeline(ctx.pipeline, ctx.exploration);
  const fields = getPipelineFields(ctx.pipeline, ctx);

  const lastGroupAggregate = getUsableGroupAggregate(flattenedPipeline);
  const { mainAxisKey, valueKeys } =
    lastGroupAggregate !== undefined
      ? amendSimpleAggregationForExistingAggregation(visualisation, lastGroupAggregate, fields)
      : amendSimpleAggregation(visualisation, fields);

  const visualisationWithoutSort = {
    mainAxisKey,
    valueKeys,
  };
  return {
    ...visualisationWithoutSort,
    sort: amendSort(visualisation.sort, visualisationWithoutSort, fields),
  };
};

export const amendAggregatedVisualisation = (
  visualisation: Omit<AggregatedVisualisation, 'viewOptions'>,
  ctx: PipelineStateContext & {
    pipeline: Pipeline;
  },
): Omit<AggregatedVisualisation, 'viewOptions'> => {
  const fields = getPipelineFields(ctx.pipeline, ctx);

  const validAggregation = {
    aggregations: ensureValidAggregations(
      visualisation.aggregation.aggregations,
      fields,
      fields,
      ctx.metrics,
    ),
    groups: ensureValidGroupings(visualisation.aggregation.groups, fields),
  };

  const visualisationWithoutSort = {
    aggregation: validAggregation,
    ...visualisationKeysFromAggregation(validAggregation),
  };
  return {
    ...visualisationWithoutSort,
    sort: amendSort(visualisation.sort, visualisationWithoutSort, fields),
  };
};

export const amendVisualisationMainAxis = (
  visualisation: ValidVisualisation,
  ctx: PipelineStateContext & { pipeline: Pipeline },
): ValidVisualisation => {
  const aggregation = isAggregatedVisualisation(visualisation)
    ? visualisation.aggregation
    : getUsableGroupAggregate(flattenPipeline(ctx.pipeline, ctx.exploration))?.parameters;
  if (aggregation === undefined) {
    return visualisation;
  }
  const { mainAxisKey } = visualisationKeysFromAggregation(aggregation);
  if (mainAxisKey === undefined) {
    return visualisation;
  }
  return { ...visualisation, mainAxisKey };
};

export const ensureValidSeries = (
  series?: VisualisationViewOptions['series'],
): VisualisationViewOptions['series'] => omitBy(series, isEmpty) ?? {};

const isGroupAggregateUsable = (
  operation: GroupAggregateOperation | undefined,
  pipeline: BasePipeline | DereferencedPipeline,
): operation is GroupAggregateOperation =>
  operation !== undefined &&
  (operation.parameters.groups.length > 0 || operation.parameters.aggregations.length > 0) &&
  pipeline.operations.indexOf(operation) > getLastSwitchToRelationIndex(pipeline.operations);

export const isBigNumberWithComparison = (
  bigNumber?: VisualisationViewOptions['bigNumber'],
): bigNumber is BigNumberWithComparisonOptions => {
  return bigNumber !== undefined && 'comparisonKey' in bigNumber;
};

export const isBigNumberWithPeriodToDateComparison = (
  bigNumber?: VisualisationViewOptions['bigNumber'],
): boolean => {
  return isBigNumberWithComparison(bigNumber) && bigNumber.comparisonMode === 'period_to_date';
};

export const addPeriodToDateOperationsToPipeline = (
  pipeline: Pipeline,
  dateFieldKey: string,
  precision: string,
): Pipeline => {
  // Generate the appropriate start_of function based on precision
  const startOfFunction = `start_of_${precision}`;

  // Create deriveField operation for elapsed time calculation
  const deriveFieldOperation = {
    operation: 'deriveField' as const,
    parameters: {
      fieldName: 'Elapsed Time Seconds',
      key: 'elapsed_time_seconds',
      value: {
        expression: `extract(prop(${dateFieldKey}) - ${startOfFunction}(prop(${dateFieldKey})), "second")`,
        version: PenguinoVersion,
      },
    },
  };

  // Create filter operation for period-to-date filtering
  const filterOperation = {
    operation: 'filter' as const,
    parameters: {
      key: 'elapsed_time_seconds',
      operator: '<' as const,
      value: {
        expression: `extract(now() - ${startOfFunction}(now()), "second")`,
        version: PenguinoVersion,
      },
    },
  };

  // Find the index of the first groupAggregate operation
  const groupAggregateIndex = pipeline.operations.findIndex(
    (op) => op.operation === 'groupAggregate',
  );

  let newOperations;
  if (groupAggregateIndex === -1) {
    // No groupAggregate found, add operations at the end
    newOperations = [...pipeline.operations, deriveFieldOperation, filterOperation];
  } else {
    // Insert operations before the groupAggregate
    newOperations = [
      ...pipeline.operations.slice(0, groupAggregateIndex),
      deriveFieldOperation,
      filterOperation,
      ...pipeline.operations.slice(groupAggregateIndex),
    ];
  }

  return {
    ...pipeline,
    operations: newOperations,
  };
};

export const setBigNumberOptions = (
  newOptions: VisualisationViewOptions,
  visualisation: ValidVisualisation,
): VisualisationViewOptions => {
  const options = {
    ...visualisation.viewOptions,
    ...newOptions,
  };

  const bigNumberKey = newOptions.bigNumber?.key;

  if (bigNumberKey === undefined) {
    return omit(options, 'bigNumber');
  }

  options.bigNumber = {
    key: bigNumberKey,
    ...(isBigNumberWithComparison(options.bigNumber)
      ? pick(options.bigNumber, [
          'comparisonKey',
          'comparisonDirection',
          'comparisonType',
          'showComparisonLabel',
          'comparisonMode',
        ])
      : {}),
    ...(options.bigNumber?.hideChart === true ? { hideChart: true } : {}),
  };

  return options;
};

export const getBigNumberHideChart = (viewOptions: VisualisationViewOptions): boolean =>
  viewOptions.bigNumber?.hideChart === true;

export const setBigNumberHideChart = (
  hideChart: boolean,
  viewOptions: VisualisationViewOptions,
  visualisation: ValidVisualisation,
): VisualisationViewOptions => {
  if (viewOptions.bigNumber === undefined) {
    throw new Error('Invalid big number state');
  }
  return setBigNumberOptions(
    { bigNumber: { ...viewOptions.bigNumber, hideChart: hideChart ? true : undefined } as any },
    visualisation,
  );
};

export const toggleVisualisationAggregation = (
  aggregated: boolean,
  visualisation: ValidVisualisation,
  fields: Field[],
) => {
  if (!aggregated && isAggregatedVisualisation(visualisation)) {
    const valueKeys = uniq(
      visualisation.aggregation.aggregations
        .filter(isKeyedAggregation)
        .reduce((acc, aggregation) => [...acc, aggregation.key], [] as string[]),
    );
    return {
      mainAxisKey: visualisation.mainAxisKey,
      valueKeys:
        valueKeys.length > 0 ? valueKeys : [fields.find(isNumericField)?.key ?? fields[0].key],
      viewOptions: visualisation.viewOptions,
    };
  }
  if (aggregated && !isAggregatedVisualisation(visualisation)) {
    const newMainAxisField =
      fields.find((field) => field.key === visualisation.mainAxisKey) ??
      fields.find(isDateField) ??
      first(fields);
    if (newMainAxisField === undefined) {
      throw new Error(`Field with key ${visualisation.mainAxisKey} not found`);
    }
    const aggregation = {
      aggregations: visualisation.valueKeys.map((valueKey) => {
        const field = fields.find((field) => field.key === valueKey);
        if (field === undefined) {
          throw new Error(`Field with key ${valueKey} not found`);
        }
        return {
          type: 'sum' as const,
          key: field.key,
          property: { key: `sum_${field.key}`, name: getAggregationLabel('sum', field) },
        };
      }),
      groups: [
        isDateField(newMainAxisField)
          ? { key: newMainAxisField.key, precision: 'month' as const, fill: true }
          : { key: newMainAxisField.key },
      ],
    };
    return {
      aggregation,
      ...visualisationKeysFromAggregation(aggregation),
      viewOptions: visualisation.viewOptions,
    };
  }
  return visualisation;
};

export const getChartType = (visualisation: ValidVisualisation, fields: Field[]) => {
  const mainAxisKey = resolveMainAxisKey(visualisation, new Set(fields.map((field) => field.key)));
  const mainAxisField = fields.find((field) => field.key === mainAxisKey);
  if (isHistogramVisualisation(visualisation, fields)) {
    return 'histogram';
  }
  if (
    mainAxisField !== undefined &&
    isDateField(mainAxisField) &&
    (!isAggregatedVisualisation(visualisation) ||
      sortDateGroupingsLast(visualisation.aggregation.groups).at(-1)?.precision !== 'day_of_week')
  ) {
    return 'grouped-timeseries';
  }
  if (mainAxisField !== undefined) {
    return 'bar';
  }
  return 'single-value';
};

const ensureUniqueAggregationPropertyKeys = (
  aggregations: Aggregation[],
  existingKeys: string[],
): Aggregation[] => {
  const ownKeys: string[] = [];
  return aggregations.map((aggregation) => {
    const uniqueKey = ensureUniqueKey([...existingKeys, ...ownKeys], aggregation.property.key);
    ownKeys.push(uniqueKey);
    return {
      ...aggregation,
      property: {
        ...aggregation.property,
        key: uniqueKey,
      },
    };
  });
};

export const setVisualisationAggregations = (
  visualisation: AggregatedVisualisation,
  aggregations: Aggregation[],
  existingKeys: string[],
) => {
  const aggregation = {
    ...visualisation.aggregation,
    aggregations: ensureUniqueAggregationPropertyKeys(aggregations, existingKeys),
  };
  const keys = visualisationKeysFromAggregation(aggregation);
  return {
    ...visualisation,
    aggregation,
    ...keys,
    viewOptions: renameSeriesKeys(
      visualisation.viewOptions,
      visualisation.valueKeys,
      keys.valueKeys,
    ),
  };
};

export const setVisualisationValueKeys = (
  visualisation: ValidVisualisation,
  valueKeys: string[],
): ValidVisualisation => {
  return {
    ...visualisation,
    valueKeys,
    viewOptions: renameSeriesKeys(visualisation.viewOptions, visualisation.valueKeys, valueKeys),
  };
};

export const setSecondaryAxisKey = (
  key: string,
  useSecondaryAxis: boolean,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  if (isCombinedKey(key)) {
    return setSeriesOptions(
      {
        ...viewOptions.series,
        [key]: {
          ...(viewOptions.series?.[key] ?? {}),
          axis: useSecondaryAxis ? 'right' : 'left',
        },
      },
      viewOptions,
    );
  }

  // Reset related secondary axis keys
  viewOptions = getRelatedSecondaryAxisKeys(key, viewOptions).reduce<VisualisationViewOptions>(
    (acc, relatedKey) => resetSeriesSecondaryAxis(relatedKey, acc),
    viewOptions,
  );
  viewOptions = resetRelatedSeriesOptions(key, 'axis', viewOptions);

  const existingKeys = viewOptions?.axes?.right?.keys ?? [];

  return {
    ...viewOptions,
    axes: {
      ...viewOptions?.axes,
      right: {
        ...viewOptions?.axes?.right,
        keys: useSecondaryAxis
          ? !existingKeys.includes(key)
            ? [...existingKeys, key]
            : existingKeys
          : existingKeys.filter((k) => k !== key),
      },
    },
  };
};

export const getActiveSeriesAxes = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): ChartAxis[] => {
  const existingKeys = viewOptions?.axes?.right?.keys ?? [];
  const relatedKeys = getRelatedSecondaryAxisKeys(key, viewOptions).concat(
    getRelatedSeriesOptionKeys(key, viewOptions),
  );
  const allKeys = relatedKeys.concat(key);

  const values: ChartAxis[] = [existingKeys.includes(key) ? 'right' : 'left'];

  // Overridden values
  entries(viewOptions.series).forEach(([k, { axis }]) => {
    if (isCombinedKey(k) && allKeys.includes(k) && axis !== undefined) {
      values.push(axis);
    }
  });

  return uniq(values);
};

/**
 * Get all secondary axis keys that are related to the given key. Related secondary axis keys are those combined keys that share the same value key.
 */
const getRelatedSecondaryAxisKeys = (key: string, viewOptions: VisualisationViewOptions = {}) =>
  (viewOptions?.axes?.right?.keys ?? []).filter((k) =>
    isCombinedKey(k) ? explodeCombinedKey(k).valueKey === key : false,
  );

const resetSeriesSecondaryAxis = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  if (viewOptions?.axes?.right?.keys === undefined) {
    return viewOptions;
  }

  return {
    ...viewOptions,
    axes: {
      ...viewOptions?.axes,
      right: {
        keys: (viewOptions?.axes?.right?.keys ?? []).filter((k) => k !== key),
      },
    },
  };
};

const setSeriesOptions = (
  seriesViewOptions: SeriesViewOptions,
  viewOptions: VisualisationViewOptions,
): VisualisationViewOptions => {
  return {
    ...viewOptions,
    series: omitBy(seriesViewOptions, isEmpty), // Remove empty values
  };
};

/**
 * Get all series options that are related to the given key. Related series options are those combined keys that share the same value key.
 */
const getRelatedSeriesOptionKeys = (key: string, viewOptions: VisualisationViewOptions = {}) =>
  keys(viewOptions.series).filter((k) =>
    isCombinedKey(k) ? explodeCombinedKey(k).valueKey === key : false,
  );

/**
 * Reset all series options that are related to the given key.
 */
export const resetSeriesOptions = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  if (!isCombinedKey(key)) {
    // Reset related series options
    viewOptions = getRelatedSeriesOptionKeys(key, viewOptions).reduce<VisualisationViewOptions>(
      (acc, relatedKey) => resetSeriesOptions(relatedKey, acc),
      viewOptions,
    );

    // Reset related secondary axis keys
    viewOptions = resetSeriesSecondaryAxis(key, viewOptions);
    viewOptions = getRelatedSecondaryAxisKeys(key, viewOptions).reduce<VisualisationViewOptions>(
      (acc, relatedKey) => resetSeriesSecondaryAxis(relatedKey, acc),
      viewOptions,
    );
  }

  return {
    ...viewOptions,
    series: omit(viewOptions?.series, key),
  };
};

const resetSeriesOption = (
  key: string,
  optionKey: keyof SeriesViewOptions[string],
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions =>
  viewOptions.series?.[key] === undefined
    ? viewOptions
    : {
        ...viewOptions,
        series: {
          ...viewOptions.series,
          [key]: omit(viewOptions.series[key], optionKey),
        },
      };

const resetRelatedSeriesOptions = (
  key: string,
  optionKey: keyof SeriesViewOptions[string],
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions =>
  isCombinedKey(key)
    ? viewOptions
    : getRelatedSeriesOptionKeys(key, viewOptions).reduce(
        (acc, relatedKey) => resetSeriesOption(relatedKey, optionKey, acc),
        viewOptions,
      );

export const setSeriesChartType = (
  key: string,
  chartType: ChartType,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  viewOptions = resetRelatedSeriesOptions(key, 'chartType', viewOptions);
  return setSeriesOptions(
    {
      ...viewOptions.series,
      [key]: {
        ...viewOptions.series?.[key],
        chartType,
      },
    },
    viewOptions,
  );
};

/**
 * Get all active chart types for the given key.
 * Active chart types are the chart types of the given key and all related series options that share the same value key.
 */
export const getActiveSeriesChartTypes = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
  defaultChartType: ChartType,
): ChartType[] => {
  const seriesOptions = viewOptions.series ?? {};
  const values = seriesOptions[key]?.chartType !== undefined ? [seriesOptions[key]?.chartType] : [];
  const relatedKeys = getRelatedSeriesOptionKeys(key, viewOptions);

  for (const [key, { chartType }] of entries(seriesOptions)) {
    if (chartType !== undefined && relatedKeys.includes(key)) {
      values.push(chartType);
    }
  }

  if (values.length === 0) {
    return [defaultChartType];
  }

  return uniq(values);
};

export const setSeriesColor = (
  key: string,
  color: string | undefined,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  viewOptions = resetRelatedSeriesOptions(key, 'color', viewOptions);
  if (color === undefined) {
    return setSeriesOptions(
      {
        ...viewOptions?.series,
        [key]: omit(viewOptions?.series?.[key], 'color'),
      },
      viewOptions,
    );
  }
  return setSeriesOptions(
    {
      ...viewOptions?.series,
      [key]: {
        ...viewOptions?.series?.[key],
        color,
      },
    },
    viewOptions,
  );
};

export const enableShowValues = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  viewOptions = resetRelatedSeriesOptions(key, 'showValues', viewOptions);
  return setSeriesOptions(
    {
      ...viewOptions?.series,
      [key]: {
        ...viewOptions?.series?.[key],
        showValues: true,
      },
    },
    viewOptions,
  );
};

export const disableShowValues = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): VisualisationViewOptions => {
  viewOptions = resetRelatedSeriesOptions(key, 'showValues', viewOptions);
  return setSeriesOptions(
    {
      ...viewOptions?.series,
      [key]: {
        ...viewOptions?.series?.[key],
        showValues: false,
      },
    },
    viewOptions,
  );
};

export const getActiveSeriesShowValues = (
  key: string,
  viewOptions: VisualisationViewOptions = {},
): boolean[] => {
  const seriesOptions = viewOptions.series ?? {};
  const values = isCombinedKey(key) ? [] : [seriesOptions[key]?.showValues ?? false];
  const relatedKeys = getRelatedSeriesOptionKeys(key, viewOptions);

  for (const [key, { showValues }] of entries(seriesOptions)) {
    if (showValues !== undefined && relatedKeys.includes(key)) {
      values.push(showValues);
    }
  }

  if (values.length === 0) {
    return [false];
  }

  return uniq(values);
};

export const visualisationKeysFromAggregation = (
  aggregation: AggregatedVisualisation['aggregation'],
) => ({
  valueKeys: aggregation.aggregations.map((aggregation) => aggregation.property.key),
  mainAxisKey: unlessNil(sortDateGroupingsLast(aggregation.groups).at(-1), (lastGroup) =>
    getGroupingOutputKey(lastGroup),
  ),
});

export const renameSeriesKeys = (
  viewOptions: VisualisationViewOptions | undefined,
  valueKeys: string[],
  newValueKeys: string[],
) => {
  if (viewOptions === undefined) {
    return undefined;
  }
  const oldKey = valueKeys.find((key) => !newValueKeys.includes(key));
  const newKey = newValueKeys.find((key) => !valueKeys.includes(key));
  if (oldKey === undefined || newKey === undefined) {
    return viewOptions; // No rename detected
  }
  // Hack: we assume only one change in the keys. A simultaneous add & remove would cause the new series to adopt the options of the removed series
  // Rename all matching series keys so all individual customized series in a multi-grouped setup are dragged along.
  const renameKey = (key: string) =>
    key
      .split(':')
      .map((part) => (part === oldKey ? newKey : part))
      .join(':');
  const axes =
    viewOptions.axes === undefined
      ? undefined
      : {
          ...viewOptions.axes,
          right: {
            keys: uniq(
              viewOptions.axes.right?.keys.map((key) => {
                return newValueKeys.includes(key) ? key : renameKey(key);
              }),
            ),
          },
        };
  const series = Object.entries(viewOptions.series ?? {}).reduce((acc, [key, value]) => {
    const updatedKey = newValueKeys.includes(key) ? key : renameKey(key);
    return { ...acc, [updatedKey]: value };
  }, {});
  return {
    ...viewOptions,
    axes,
    series,
  };
};
